import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  model,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'

import { FlxButtonModule } from '@flowx/angular-ui-toolkit'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { ProcessDefinition } from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ModalPanelComponent } from '@flowx/designer/shared/components/modal-panel/modal-panel.component'
import { FlxResourceInstancesAutocompleteComponent } from '@flowx/designer/shared/components/resource-instances-autocomplete/resource-instances-autocomplete.component'
import { ProcessInstance } from '@flowx/designer/shared/entities/process-instance.types'
import { ProcessInstanceService } from '@flowx/designer/shared/services/process-instance.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { CodeEditorComponent } from '../code-editor/code-editor.component'

type ProcessInstanceSummary = Pick<ProcessInstance, 'uuid' | 'state' | 'dateStarted'>

@Component({
  selector: 'flx-scripting-input-data',
  imports: [
    FormsModule,
    CodeEditorComponent,
    FlxResourceInstancesAutocompleteComponent,
    FlxButtonModule,
    ModalPanelComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './scripting-input-data.component.html',
})
export class ScriptingInputDataComponent {
  #destroyRef = inject(DestroyRef)
  #processInstancesService = inject(ProcessInstanceService)
  #toastMessageService = inject(ToastMessageService)
  #analyticsService = inject(AnalyticsService)

  data = model<string>('')
  processVersionId = input<ProcessDefinition['id']>(null)
  processDefinitionName = input<ProcessDefinition['resourceName']>(null)
  selectedProcessInstanceUuid = signal<string>('')
  processInstanceUuidValid = signal<boolean>(false)

  latestLoadedData = signal<{
    processInstanceUuid: ProcessInstanceSummary['uuid']
    data: string
  }>({
    processInstanceUuid: null,
    data: '',
  })

  disabledProcessInstanceLoad = computed(() => {
    const processInstanceUuidValid = this.processInstanceUuidValid()
    const latestLoadedData = this.latestLoadedData()
    const data = this.data()
    const selectedProcessInstanceUuid = this.selectedProcessInstanceUuid()

    const sameData = latestLoadedData?.data === data

    return (
      !processInstanceUuidValid ||
      (!!sameData && selectedProcessInstanceUuid === latestLoadedData?.processInstanceUuid)
    )
  })

  codeEditorOptions = {
    language: 'json',
  }

  setProcessInstance(event: { instanceUuid: string; valid: boolean }): void {
    this.processInstanceUuidValid.set(event.valid)
    this.selectedProcessInstanceUuid.set(event.instanceUuid)
  }

  loadProcessInstanceData(): void {
    this.#analyticsService.trackUntyped({ object: 'test_expression', action: 'data_imported' })
    this.#toastMessageService.showWarningMessage('Fetching instance data...')

    const processInstanceUuid = this.selectedProcessInstanceUuid()
    this.#processInstancesService
      .getProcessInstanceParamsByUuId(processInstanceUuid)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((processInstanceData) => {
        const stringJson = JSON.stringify(processInstanceData ?? {}, undefined, 2)
        this.data.set(stringJson)
        this.latestLoadedData.update(() => ({ processInstanceUuid, data: stringJson }))
      })
  }
}
